import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  Bell,
  Calendar,
  ClipboardList,
  Eye,
  FileText,
  MessageSquare,
  Plus,
  Scale,
} from "lucide-react";
import { useUpcomingDeadlines } from "@/hooks/use-analytics";
import { useUnreadCount } from "@/hooks/use-notifications";
import { useAuth } from "@/components/providers/auth-provider";
import { RecentActivity } from "@/components/dashboard/recent-activity";

// Dashboard rediseñado: minimalista y accionable. Fuera onboarding, fuera
// gráficos genéricos. El cliente quiere ver de un vistazo qué cierra
// pronto, qué actividad ha pasado y tener atajos para lo más usado.

interface DeadlineItem {
  licitacion_id: string;
  titulo: string;
  organismo?: string;
  fecha_presentacion: string;
  dias_restantes: number;
  estado: string;
}

function saludo(name?: string): string {
  const h = new Date().getHours();
  const tramo = h < 12 ? "Buenos días" : h < 20 ? "Buenas tardes" : "Buenas noches";
  const nombre = (name || "").trim().split(/\s+/)[0];
  return nombre ? `${tramo}, ${nombre}` : tramo;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function urgencyClasses(dias: number): { badge: string; dot: string } {
  if (dias < 7) return { badge: "bg-danger/12 text-danger", dot: "bg-danger" };
  if (dias <= 14) return { badge: "bg-warning/15 text-warning", dot: "bg-warning" };
  return { badge: "bg-success/12 text-success", dot: "bg-success" };
}

const ESTADO_LABELS: Record<string, string> = {
  BORRADOR: "Borrador",
  EN_ANALISIS: "En análisis",
  EN_PREPARACION: "En preparación",
  PRESENTADA: "Presentada",
  EN_EVALUACION: "En evaluación",
  ADJUDICADA: "Adjudicada",
  RECHAZADA: "Rechazada",
  DESIERTA: "Desierta",
  CANCELADA: "Cancelada",
};

export default function DashboardPage() {
  const { user } = useAuth();
  const { data: deadlinesRaw = [], isLoading: loadingDeadlines } =
    useUpcomingDeadlines(30);
  const { data: unreadCount } = useUnreadCount();

  // El backend devuelve un array, pero por si acaso normalizamos.
  const deadlines: DeadlineItem[] = useMemo(
    () => (Array.isArray(deadlinesRaw) ? deadlinesRaw : []),
    [deadlinesRaw]
  );

  const urgentes = useMemo(
    () => deadlines.filter((d) => d.dias_restantes <= 7),
    [deadlines]
  );
  const topDeadlines = useMemo(() => deadlines.slice(0, 6), [deadlines]);
  const unread = unreadCount?.count ?? 0;

  return (
    <div className="flex flex-col gap-8 p-8 max-w-container mx-auto">
      {/* Cabecera con saludo personalizado. Sin párrafo redundante: el
          cliente sabe qué es la plataforma. */}
      <header className="flex flex-col gap-1">
        <p className="text-xs uppercase tracking-[0.18em] font-heading font-semibold text-brand">
          Panel
        </p>
        <h1 className="text-3xl font-heading font-semibold text-ink">
          {saludo(user?.name || user?.email)}
        </h1>
      </header>

      {/* Banner de urgencias: solo aparece si hay licitaciones cerrando en
          ≤7 días. Es accionable y desaparece cuando todo está bajo control. */}
      {urgentes.length > 0 && (
        <div className="rounded border-l-4 border-danger bg-danger/8 px-4 py-3 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-content">
            <strong className="font-heading text-ink">
              {urgentes.length === 1
                ? "1 licitación cierra esta semana"
                : `${urgentes.length} licitaciones cierran esta semana`}
            </strong>
            <p className="text-content-muted mt-0.5">
              Revisa el estado de tus propuestas para no quedarte fuera por
              plazo.
            </p>
          </div>
          <Link
            to="/licitaciones?orden=fecha_limite"
            className="text-sm font-heading font-semibold text-brand hover:text-brand-hover whitespace-nowrap"
          >
            Ver licitaciones →
          </Link>
        </div>
      )}

      {/* Accesos rápidos. 4 tarjetas con las acciones que el cliente hace
          a diario. Diseño en línea con el handoff (paneles claros, hover
          eleva con shadow-card). */}
      <section>
        <h2 className="text-[11px] uppercase tracking-[0.18em] font-heading font-semibold text-content-muted mb-3">
          Atajos
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <QuickAction
            to="/licitaciones/nueva"
            icon={Plus}
            title="Nueva licitación"
            sub="Crear o clonar"
            primary
          />
          <QuickAction
            to="/licitaciones"
            icon={ClipboardList}
            title="Licitaciones"
            sub="Ver el listado completo"
          />
          <QuickAction
            to="/chat"
            icon={MessageSquare}
            title="Chat IA"
            sub="Pregunta sobre pliegos"
          />
          <QuickAction
            to="/configuracion/marco-legal"
            icon={Scale}
            title="Marco legal"
            sub="Normativa vigente"
          />
        </div>
      </section>

      {/* Bloque principal: vencimientos + actividad. En desktop quedan en
          dos columnas; en móvil se apilan. */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Próximos vencimientos */}
        <section className="lg:col-span-2 bg-surface border border-line rounded shadow-card overflow-hidden">
          <header className="px-5 py-4 border-b border-line flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-brand" />
              <h2 className="font-heading font-semibold text-base text-ink">
                Próximos vencimientos
              </h2>
            </div>
            <Link
              to="/licitaciones"
              className="text-xs font-heading font-semibold text-brand hover:text-brand-hover"
            >
              Ver todas <ArrowRight className="w-3 h-3 inline -mt-0.5" />
            </Link>
          </header>
          <div className="divide-y divide-line">
            {loadingDeadlines && (
              <ul>
                {[0, 1, 2].map((i) => (
                  <li key={i} className="px-5 py-3.5 animate-pulse">
                    <div className="h-3 w-2/3 bg-surface-200 rounded mb-1.5" />
                    <div className="h-2.5 w-1/3 bg-surface-200 rounded" />
                  </li>
                ))}
              </ul>
            )}
            {!loadingDeadlines && topDeadlines.length === 0 && (
              <div className="px-5 py-10 text-center text-sm text-content-muted">
                Sin vencimientos en los próximos 30 días.
              </div>
            )}
            {!loadingDeadlines &&
              topDeadlines.map((d) => {
                const u = urgencyClasses(d.dias_restantes);
                return (
                  <Link
                    key={d.licitacion_id}
                    to={`/licitaciones/${d.licitacion_id}`}
                    className="flex items-center gap-4 px-5 py-3.5 hover:bg-surface-100 transition-colors group"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-heading font-semibold text-sm text-ink truncate">
                        {d.titulo}
                      </p>
                      <p className="text-xs text-content-muted mt-0.5 flex items-center gap-2">
                        {d.organismo && (
                          <>
                            <span className="truncate">{d.organismo}</span>
                            <span className="text-line">·</span>
                          </>
                        )}
                        <span className="whitespace-nowrap">
                          {formatDate(d.fecha_presentacion)}
                        </span>
                        <span className="text-line">·</span>
                        <span className="whitespace-nowrap uppercase tracking-wider text-[10px] font-heading font-semibold">
                          {ESTADO_LABELS[d.estado] ?? d.estado}
                        </span>
                      </p>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-pill text-[11px] font-heading font-semibold whitespace-nowrap ${u.badge}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${u.dot}`} />
                      {d.dias_restantes <= 0
                        ? "Hoy"
                        : d.dias_restantes === 1
                          ? "1 día"
                          : `${d.dias_restantes} días`}
                    </span>
                    <Eye className="w-4 h-4 text-content-muted opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                );
              })}
          </div>
        </section>

        {/* Sidebar derecha: notificaciones + actividad */}
        <aside className="space-y-6">
          <div className="bg-surface border border-line rounded shadow-card overflow-hidden">
            <Link
              to="/notificaciones"
              className="block px-5 py-4 hover:bg-surface-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-9 h-9 rounded-full grid place-items-center flex-shrink-0 ${unread > 0 ? "bg-brand/12 text-brand" : "bg-surface-200 text-content-muted"}`}
                >
                  <Bell className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-semibold text-sm text-ink">
                    {unread > 0
                      ? `${unread} notificación${unread === 1 ? "" : "es"} sin leer`
                      : "Sin notificaciones nuevas"}
                  </p>
                  <p className="text-xs text-content-muted">
                    Ver todas las notificaciones
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-content-muted" />
              </div>
            </Link>
          </div>

          <div className="bg-surface border border-line rounded shadow-card overflow-hidden">
            <header className="px-5 py-4 border-b border-line flex items-center gap-2">
              <FileText className="w-4 h-4 text-brand" />
              <h2 className="font-heading font-semibold text-base text-ink">
                Actividad reciente
              </h2>
            </header>
            <RecentActivity />
          </div>
        </aside>
      </div>
    </div>
  );
}

interface QuickActionProps {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  sub: string;
  primary?: boolean;
}

function QuickAction({ to, icon: Icon, title, sub, primary }: QuickActionProps) {
  return (
    <Link
      to={to}
      className={`group flex items-center gap-3 rounded border px-4 py-3.5 transition-all hover:-translate-y-0.5 hover:shadow-card ${primary ? "bg-brand text-white border-brand-border hover:bg-brand-hover" : "bg-surface border-line hover:border-brand-muted"}`}
    >
      <div
        className={`w-8 h-8 rounded grid place-items-center flex-shrink-0 ${primary ? "bg-white/15 text-white" : "bg-brand/12 text-brand"}`}
      >
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`font-heading font-semibold text-sm ${primary ? "text-white" : "text-ink"}`}>
          {title}
        </p>
        <p className={`text-xs ${primary ? "text-white/80" : "text-content-muted"} truncate`}>
          {sub}
        </p>
      </div>
    </Link>
  );
}
